import React, { useCallback, useMemo, useState } from 'react';
import { Linking, View } from 'react-native';
import { useMutation, useQuery } from '@apollo/client';
import { useFocusEffect } from '@react-navigation/native';
import moment from 'moment';
import Toast from 'react-native-root-toast';

import {
	CLIENT_LEAD_STATUS,
	CLIENT_LEAD_STATUS_NAMES,
} from '../../../common/constants';
import { CREATE_NOTE } from '../../../common/graphql/note';
import { getUserName } from '../../../common/utils/user/getUserName';
import { useUserContext } from '../../../common/utils/context';
import {
	GET_MOBILE_LEAD,
	GET_MOBILE_LEAD_ASSIGNEES,
	MODIFY_MOBILE_LEAD,
} from '../../graphql/lead';
import { Button, Picker, Text, TextInput } from '../../components';
import { VisualDirectionPhotoGrid } from '../../components/image';
import { Alert } from '../../components/other/Alert';
import { UserSelector } from '../../components/selector';
import withMediaPicker from '../../hocs/withMediaPicker';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { Card, EmptyState, Screen, SecondaryButton, SectionTitle } from '../../design/components';
import { useOfflineQueue } from '../../offline';
import LeadContactBlock from './LeadContactBlock';
import { EMPTY_LEAD_CONTACT, getLeadContactInput } from './LeadContactFields';
import { createLeadDetailStyles } from './LeadDetailView.styles';
import LeadFollowUpDateInput, { FOLLOW_UP_DATE_FORMAT } from './LeadFollowUpDateInput';
import LeadTextAction from './LeadTextAction';
import useLeadPhotos from './useLeadPhotos';

const STATUS_OPTIONS = [
	CLIENT_LEAD_STATUS.NEW_LEAD,
	CLIENT_LEAD_STATUS.CONTACTED,
	CLIENT_LEAD_STATUS.HOT_LEAD,
	CLIENT_LEAD_STATUS.WARM_LEAD,
	CLIENT_LEAD_STATUS.COLD_LEAD,
	CLIENT_LEAD_STATUS.ON_PAUSE,
	CLIENT_LEAD_STATUS.DEAD_LEAD,
	CLIENT_LEAD_STATUS.ISSUE_NOT_A_LEAD,
].map(value => ({
	label: CLIENT_LEAD_STATUS_NAMES[value],
	value,
}));

const ASSIGNEE_FILTER = {
	enabled: undefined,
	excludeFromSelectors: undefined,
	clientsLeadAssigneeEligible: true,
	crossTenant: true,
};

const LeadDetailView = ({
	route,
	captureMedia,
	capturedMediaBatch,
	processingMedia,
}) => {
	const styles = useThemedStyles(createLeadDetailStyles);
	const { canViewClientsLeads, canManageClientsLeads } = useUserContext();
	const { isOnline } = useOfflineQueue();
	const id = route?.params?.id;
	const [noteDescription, setNoteDescription] = useState('');
	// 'person' or 'secondaryPerson' while that contact's form is open.
	const [editingContactField, setEditingContactField] = useState(null);
	const [editingContactId, setEditingContactId] = useState(null);
	const [contactDraft, setContactDraft] = useState(EMPTY_LEAD_CONTACT);
	const { data, error, loading, refetch } = useQuery(GET_MOBILE_LEAD, {
		variables: { id },
		skip: !id || !canViewClientsLeads,
		fetchPolicy: 'cache-and-network',
		notifyOnNetworkStatusChange: true,
	});
	const [modifyLead, modifyStatus] = useMutation(MODIFY_MOBILE_LEAD);
	const [createNote, createNoteStatus] = useMutation(CREATE_NOTE);
	const lead = data?.getLead || null;

	useFocusEffect(useCallback(() => {
		if (id && canViewClientsLeads) {
			refetch().catch(() => {});
		}
	}, [canViewClientsLeads, id, refetch]));

	const updateLead = useCallback(async (values, successMessage) => {
		if (!id || !canManageClientsLeads || modifyStatus.loading) {
			return false;
		}

		try {
			await modifyLead({
				variables: {
					input: {
						id,
						...values,
					},
				},
			});
			await refetch();
			Toast.show(successMessage);
			return true;
		}
		catch (mutationError) {
			Toast.show(mutationError?.message || 'Unable to update lead');
			return false;
		}
	}, [canManageClientsLeads, id, modifyLead, modifyStatus.loading, refetch]);

	const requireOnline = useCallback((message) => {
		if (!isOnline) {
			Alert.alert('Internet connection required', message);
		}

		return isOnline;
	}, [isOnline]);

	const changeFollowUpDate = useCallback((value) => {
		const currentValue = lead?.nextFollowUpAt ? moment(lead.nextFollowUpAt).format(FOLLOW_UP_DATE_FORMAT) : '';
		if (value === currentValue || !requireOnline('The next follow-up date needs internet to save.')) {
			return;
		}

		updateLead(
			{ nextFollowUpAt: value ? moment(value, FOLLOW_UP_DATE_FORMAT).startOf('day').toDate() : null },
			value ? 'Next follow-up updated' : 'Next follow-up cleared',
		);
	}, [lead?.nextFollowUpAt, requireOnline, updateLead]);

	const openContactForm = useCallback((field) => {
		const contact = lead?.[field];
		setContactDraft({
			name: contact?.name || '',
			role: contact?.role || '',
			phoneNumber: contact?.phoneNumber || '',
			email: contact?.email || '',
		});
		setEditingContactId(contact?.id || null);
		setEditingContactField(field);
	}, [lead]);

	const changeContactDraft = useCallback((field, value) => {
		setContactDraft(current => ({ ...current, [field]: value }));
	}, []);

	const saveContact = useCallback(async () => {
		if (!editingContactField || !requireOnline('Contacts need internet to save.')) {
			return;
		}

		// Keep the id captured when the form opened, so a refetch cannot point this draft at a different contact.
		const contactId = editingContactId;
		const isSaved = await updateLead(
			{
				// Sending the shown contact's id lets the server refuse the save if the lead's contact changed meanwhile.
				...(contactId ? { [editingContactField]: contactId } : {}),
				...getLeadContactInput(contactDraft, editingContactField),
			},
			contactId ? 'Contact updated' : 'Contact added',
		);
		if (isSaved) {
			setEditingContactField(null);
		}
	}, [contactDraft, editingContactField, editingContactId, requireOnline, updateLead]);

	const changeStatus = useCallback((status) => {
		if (status !== lead?.status) {
			updateLead({ status }, 'Lead status updated');
		}
	}, [lead?.status, updateLead]);

	const changeAssignee = useCallback((assignee) => {
		if (assignee?.id && assignee.id !== lead?.assignee?.id) {
			updateLead({ assignee: assignee.id }, 'Lead assigned');
		}
	}, [lead?.assignee?.id, updateLead]);

	const clearAssignee = useCallback(() => {
		if (lead?.assignee?.id) {
			updateLead({ assignee: null }, 'Lead unassigned');
		}
	}, [lead?.assignee?.id, updateLead]);

	const addNote = useCallback(async () => {
		const description = noteDescription.trim();
		if (!description || !id || !canManageClientsLeads || createNoteStatus.loading) {
			return;
		}

		try {
			await createNote({
				variables: {
					input: {
						lead: id,
						description,
					},
				},
			});
			setNoteDescription('');
			await refetch();
			Toast.show('Contact note added');
		}
		catch (mutationError) {
			Toast.show(mutationError?.message || 'Unable to add contact note');
		}
	}, [canManageClientsLeads, createNote, createNoteStatus.loading, id, noteDescription, refetch]);

	const { isSavingPhotos, addPhotos, removePhoto } = useLeadPhotos({
		id,
		photos: lead?.photos,
		isOnline,
		captureMedia,
		capturedMediaBatch,
		processingMedia,
		modifyLead,
		refetch,
		updateLead,
	});

	const openContactLink = useCallback(async (url, errorMessage) => {
		try {
			await Linking.openURL(url);
		}
		catch (linkError) {
			Toast.show(errorMessage);
		}
	}, []);

	const notes = useMemo(() => (
		[...(lead?.notes || [])].sort((left, right) => (
			new Date(right.created).getTime() - new Date(left.created).getTime()
		))
	), [lead?.notes]);

	if (!canViewClientsLeads) {
		return (
			<Screen backLabel="Leads">
				<EmptyState icon="lock" title="No access" message="You do not have access to leads." />
			</Screen>
		);
	}

	if (loading && !lead) {
		return <Screen backLabel="Leads" isLoading={true} />;
	}

	if (error || !lead) {
		return (
			<Screen backLabel="Leads">
				<EmptyState tone="error" title="Lead unavailable" message="Go back and open the lead again." />
			</Screen>
		);
	}

	const contactBlockProps = {
		editingContactField,
		canManageClientsLeads,
		contactDraft,
		onChangeContactDraft: changeContactDraft,
		isSaving: modifyStatus.loading,
		onCancelEdit: () => setEditingContactField(null),
		onSaveContact: saveContact,
		onOpenContactForm: openContactForm,
		onOpenContactLink: openContactLink,
	};

	return (
		<Screen backLabel="Leads">
			<Card style={styles.hero}>
				<Text style={styles.kicker}>Sales lead</Text>
				<Text style={styles.title} accessibilityRole="header">{lead.name || 'Unnamed lead'}</Text>
				<View style={styles.heroMetaRow}>
					<Text style={styles.heroMeta}>{lead.suburb || lead.state || 'Location not recorded'}</Text>
					<Text style={styles.heroMeta}>Created {moment(lead.created).format('DD MMM YYYY')}</Text>
				</View>
			</Card>

			<Card>
				<SectionTitle title="Pipeline" style={styles.sectionTitle} />
				<View style={styles.field}>
					<Text style={styles.fieldLabel}>Status</Text>
					{
						canManageClientsLeads ? (
							<Picker
								options={STATUS_OPTIONS}
								selectedValue={lead.status}
								onValueChange={changeStatus}
								disabled={modifyStatus.loading}
								width="100%"
							/>
						) : (
							<Text style={styles.readOnlyValue}>{CLIENT_LEAD_STATUS_NAMES[lead.status] || 'Unknown status'}</Text>
						)
					}
				</View>
				<View style={styles.field}>
					<Text style={styles.fieldLabel}>Assignee</Text>
					{
						canManageClientsLeads ? (
							<>
								<UserSelector
									value={lead.assignee}
									onChange={changeAssignee}
									placeholder="Select assignee"
									query={GET_MOBILE_LEAD_ASSIGNEES}
									initialFilter={ASSIGNEE_FILTER}
									lazyLoad={true}
									disabled={modifyStatus.loading}
									buttonStyle={styles.selectorButton}
								/>
								{
									Boolean(lead.assignee) && (
										<LeadTextAction
											label="Clear assignee"
											onPress={clearAssignee}
											disabled={modifyStatus.loading}
										/>
									)
								}
							</>
						) : (
							<Text style={styles.readOnlyValue}>{lead.assignee ? getUserName(lead.assignee) : 'Unassigned'}</Text>
						)
					}
				</View>
				<View style={styles.field}>
					<Text style={styles.fieldLabel}>Next follow-up</Text>
					{
						canManageClientsLeads ? (
							<>
								<LeadFollowUpDateInput
									value={lead.nextFollowUpAt ? moment(lead.nextFollowUpAt).format(FOLLOW_UP_DATE_FORMAT) : ''}
									onChange={changeFollowUpDate}
									disabled={modifyStatus.loading}
								/>
								<Text style={styles.fieldHint}>
									The assignee gets a reminder email on this date, then one each day until the date is changed or the lead is closed or secured.
								</Text>
							</>
						) : (
							<Text style={styles.readOnlyValue}>
								{lead.nextFollowUpAt ? moment(lead.nextFollowUpAt).format('DD MMM YYYY') : 'Not set'}
							</Text>
						)
					}
				</View>
				<View style={styles.lastContact}>
					<Text style={styles.fieldLabel}>Last contact</Text>
					<Text style={styles.readOnlyValue}>
						{lead.lastContactAt ? moment(lead.lastContactAt).format('DD MMM YYYY') : 'No notes yet'}
					</Text>
				</View>
			</Card>

			<Card>
				<SectionTitle title="Contacts" style={styles.sectionTitle} />
				<LeadContactBlock {...contactBlockProps} contact={lead.person} label="Primary contact" field="person" />
				<LeadContactBlock
					{...contactBlockProps}
					contact={lead.secondaryPerson}
					label="Secondary contact"
					field="secondaryPerson"
					isLast={true}
				/>
			</Card>

			{
				Boolean(lead.message || lead.companySize) && (
					<Card>
						<SectionTitle title="Enquiry" style={styles.sectionTitle} />
						{Boolean(lead.companySize) && <Text style={styles.enquiryMeta}>Company size: {lead.companySize}</Text>}
						{Boolean(lead.message) && <Text style={styles.enquiryText}>{lead.message}</Text>}
					</Card>
				)
			}

			<Card>
				<SectionTitle title="Photos" style={styles.sectionTitle} />
				<VisualDirectionPhotoGrid
					canEdit={canManageClientsLeads && !isSavingPhotos && !modifyStatus.loading}
					emptyText="No photos yet. Add business cards or machines on site."
					onRemovePhoto={removePhoto}
					photos={lead.photos}
				/>
				{
					canManageClientsLeads && (
						<SecondaryButton
							label="Add photos"
							icon="camera"
							onPress={addPhotos}
							loading={processingMedia || isSavingPhotos}
							disabled={processingMedia || isSavingPhotos}
							style={styles.addPhotosButton}
						/>
					)
				}
			</Card>

			<Card>
				<SectionTitle title="Contact notes" style={styles.sectionTitle} />
				{
					canManageClientsLeads && (
						<View style={styles.noteComposer}>
							<TextInput
								value={noteDescription}
								onChange={setNoteDescription}
								placeholder="Add details from a call, email or follow-up"
								multiline={true}
								maxLength={3000}
								style={styles.noteInput}
							/>
							<Button
								label="Add contact note"
								onPress={addNote}
								secondary={true}
								loading={createNoteStatus.loading}
								disabled={!noteDescription.trim() || createNoteStatus.loading}
							/>
						</View>
					)
				}
				{
					notes.length ? notes.map((note, index) => (
						<View key={note.id} style={[styles.note, index === notes.length - 1 && styles.noteLast]}>
							<Text style={styles.noteText}>{note.description}</Text>
							<Text style={styles.noteMeta}>
								{note.creator ? getUserName(note.creator) : 'Unknown user'} · {moment(note.created).format('DD MMM YYYY, HH:mm')}
							</Text>
						</View>
					)) : (
						<Text style={styles.emptyText}>No contact notes</Text>
					)
				}
			</Card>
		</Screen>
	);
};

const LeadDetailViewWithMediaPicker = withMediaPicker(LeadDetailView);

const LeadDetailViewWithPicker = props => (
	<LeadDetailViewWithMediaPicker
		s3Path="images/leads/"
		allowMultipleSelection={true}
		allowMultipleCapture={true}
		collectCapturedMediaBatch={true}
		{...props}
	/>
);

export default LeadDetailViewWithPicker;
